import type { Dict } from '../utils'
import type { SvgState } from './svg.types'

import { __DEV__ } from '../utils'

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'

/**
 * SVG elements allowed in icon content. Notably excludes 'script' and
 * 'foreignObject' (which switches parsing back to HTML and enables
 * DOM clobbering / XSS). Comparison is case-sensitive per XML parsing.
 */
const ALLOWED_ELEMENTS = new Set([
  'svg',
  'g',
  'path',
  'circle',
  'ellipse',
  'rect',
  'line',
  'polyline',
  'polygon',
  'defs',
  'title',
  'desc',
  'symbol',
  'use',
  'linearGradient',
  'radialGradient',
  'stop',
  'clipPath',
  'mask',
  'pattern',
  'marker',
  'text',
  'tspan',
])

function warnRemoval(kind: 'element' | 'attribute', name: string) {
  if (__DEV__) console.warn(`[Svg] Removed unsafe SVG ${kind} \`${name}\`.`)
}

/** Removes disallowed elements, event handler attributes and external references. */
function sanitizeElement(element: Element) {
  if (element.namespaceURI !== SVG_NAMESPACE || !ALLOWED_ELEMENTS.has(element.tagName)) {
    warnRemoval('element', element.tagName)
    element.remove()
    return
  }

  for (const attr of Array.from(element.attributes)) {
    const name = attr.localName.toLowerCase()
    const isEventHandler = name.startsWith('on')
    const isExternalRef = name === 'href' && !attr.value.trim().startsWith('#')

    if (isEventHandler || isExternalRef) {
      warnRemoval('attribute', attr.name)
      element.removeAttribute(attr.name)
    }
  }

  for (const child of Array.from(element.children)) {
    sanitizeElement(child)
  }
}

/** Parses an SVG string into an inert, sanitized svg element. Returns undefined for invalid content. */
export function parseSvg(html: string) {
  // DOMParser produces an inert document: nothing executes or loads while parsing
  const doc = new DOMParser().parseFromString(html, 'image/svg+xml')
  const root = doc.documentElement

  // A parse error yields a 'parsererror' root, which also fails this check
  if (root.namespaceURI !== SVG_NAMESPACE || root.tagName !== 'svg') return undefined

  sanitizeElement(root)

  return root
}

/** Returns an object with given element's HTML attributes. */
export function getAttributes(element?: Element) {
  if (!element) return {}

  const attrs = Array.from(element.attributes)

  return attrs.reduce((props: Dict<string>, attr) => {
    const { name, value } = attr
    props[name] = value
    return props
  }, {})
}

/** Returns the inner content of a sanitized svg element. */
export function getSvgContent(element?: Element) {
  return element?.innerHTML ?? ''
}

/** Returns object with initial state values. */
export function initState(): SvgState {
  return { content: '', svgAttributes: {} }
}
